import * as classnames from 'classnames';
import React from 'react';
import { useSelector } from 'react-redux';
import { ChannelIcon } from '../../../editor/features/liveChat/components/ChannelIcon';
import { EnhancedBillingPage } from '@mm/state/interfaces/connected-page';
import { StoreState } from '@mm/state/store-interfaces';
import { abbreviateNumber } from '@mm/utils';
import { usePhoneNumber } from '../../hooks/use-phone-number';
import './UsagePercentageBar.pcss';
import { getUsedAndTotal } from '../../../dashboard/pages/SettingsPage/BillingPanel/SubscriptionTabsArea/ActiveSubscriptionTab/helpers';

type Props = {
  className: string;
  fbPage: EnhancedBillingPage;
  isTeam?: boolean;
  isCustomTeam?: boolean;
};
// tslint:disable-next-line:cyclomatic-complexity
export const UsagePercentageBar = (props: Props) => {
  const { className, isTeam, isCustomTeam, fbPage } = props;
  const { messagesStatistic: statistics, subscription } = fbPage;
  const [used, total] = getUsedAndTotal(statistics, subscription);
  const creditsRatio = useSelector(
    (state: StoreState) => state.env.creditsQuantity,
  );
  const { isSmsSupported } = usePhoneNumber();
  const showNAForCustomTeam =
    isCustomTeam && fbPage.lockStrategy === 'twilio_level';
  const infinity = <span>&infin;</span>;

  const classList = classnames('usage-percentage-bar', className);

  const messagesLeft = total - used > 0 ? total - used : 0;

  const getFormatedCountLeft = (ratio: number) => {
    const messagesOfThisTypeLeft = Math.floor(messagesLeft / ratio);
    return ` ${abbreviateNumber(messagesOfThisTypeLeft)} `;
  };

  const messageTypeClassNames = classnames(
    'usage-percentage-bar__messages-type',
    {
      'usage-percentage-bar__messages-type-limit-exceeded': messagesLeft === 0,
    },
  );

  const sendCreditsUsed =
    (isTeam
      ? fbPage?.outgoingMessageUsage?.prevSubscriptionMessagingCount
      : fbPage?.prevOutgoingMessagesStatistic?.count) || 0;

  const showSendCreditsUsed = isTeam || fbPage?.prevOutgoingMessagesStatistic;

  const billingPeriod = subscription?.billingPeriodLength;
  const customPeriodLength = subscription?.planIntervalLength;

  const period =
    billingPeriod === 'annually'
      ? 'year'
      : billingPeriod === 'monthly' && (customPeriodLength || 0) > 1
      ? `period`
      : 'month';

  return (
    <div className={classList}>
      <div className="usage-percentage-bar__body-wrapper">
        <div className="usage-percentage-bar__messages-left">
          Messages left:
          <div className="usage-percentage-bar__stats">
            <div className="usage-percentage-bar__stats-row">
              <ChannelIcon
                className="usage-percentage-bar__icon-svg"
                channel="email"
              />
              <div className={messageTypeClassNames}>
                {showNAForCustomTeam
                  ? infinity
                  : getFormatedCountLeft(creditsRatio?.facebookPage ?? 1)}{' '}
                <span className="usage-percentage-bar__text-small">emails</span>
              </div>
            </div>

            <div className="usage-percentage-bar__stats-row">
              <ChannelIcon
                className="usage-percentage-bar__icon-svg"
                channel="webchat"
              />
              <div className={messageTypeClassNames}>
                {showNAForCustomTeam
                  ? infinity
                  : getFormatedCountLeft(creditsRatio?.web || 1)}{' '}
                <span className="usage-percentage-bar__text-small">
                  messages
                </span>
              </div>
            </div>

            <div className="usage-percentage-bar__stats-row">
              <ChannelIcon
                className="usage-percentage-bar__icon-svg"
                channel="facebook"
              />
              <div className={messageTypeClassNames}>
                {showNAForCustomTeam
                  ? infinity
                  : getFormatedCountLeft(creditsRatio?.facebookPage ?? 1)}{' '}
                <span className="usage-percentage-bar__text-small">
                  messages
                </span>
              </div>
            </div>

            {isSmsSupported && !fbPage.redemption?.instachamp && !fbPage.free && (
              <div className="usage-percentage-bar__stats-row">
                <div className="usage-percentage-bar__sms-info">
                  <ChannelIcon
                    className="usage-percentage-bar__icon-svg"
                    channel="sms"
                  />
                  <div className={messageTypeClassNames}>
                    {getFormatedCountLeft(creditsRatio?.sms || 10)}
                    <span className="usage-percentage-bar__text-small">
                      SMS
                    </span>
                    <span className="usage-percentage-bar__text-small-secondary">
                      1 SMS - {creditsRatio?.sms || 10} credits
                    </span>
                  </div>
                </div>
              </div>
            )}

            <div className="usage-percentage-bar__stats-row">
              <div className="usage-percentage-bar__instagram-info">
                <div className="usage-percentage-bar__channel-stats">
                  <ChannelIcon
                    className="usage-percentage-bar__icon-svg"
                    channel="instagram"
                  />
                  <div className={messageTypeClassNames}>
                    {showNAForCustomTeam
                      ? infinity
                      : getFormatedCountLeft(creditsRatio?.instagram || 1)}{' '}
                    <span className="usage-percentage-bar__text-small">
                      messages
                    </span>
                  </div>
                </div>
              </div>
            </div>

            {isSmsSupported && !fbPage.redemption?.instachamp && !fbPage.free && (
              <div className="usage-percentage-bar__stats-row">
                <div className="usage-percentage-bar__sms-info">
                  <ChannelIcon
                    className="usage-percentage-bar__icon-svg"
                    channel="sms"
                  />
                  <div className={messageTypeClassNames}>
                    {getFormatedCountLeft(creditsRatio?.mms || 20)}
                    <span className="usage-percentage-bar__text-small">
                      MMS
                    </span>
                    <span className="usage-percentage-bar__text-small-secondary">
                      1 MMS - {creditsRatio?.mms || 20} credits
                    </span>
                  </div>
                </div>
              </div>
            )}
          </div>
          {showSendCreditsUsed && (
            <div className="usage-percentage-bar__messages-left--label">
              Credits used last {period}:{' '}
              <span style={{ fontWeight: 500 }}>
                {abbreviateNumber(sendCreditsUsed)}
              </span>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};
